// Types de l'ÉTAT de la formation : un seul objet JSON, identique dans le navigateur
// (IndexedDB) et sur le serveur (table `etats`). Il n'est jamais modifié directement :
// il est recalculé en appliquant des événements (voir evenements.ts et reducteurs.ts).
// Spécification : docs/specs-fonctionnelles.md §6.

export type Domaine = 'AG' | 'EL' | 'PR' | 'LA' | 'IA' | 'CA';

export type ModeSession = 'standard' | 'faible_energie';
export type StatutSession = 'a_faire' | 'en_cours' | 'terminee' | 'abandonnee';

export interface ProgressionSession {
  statut: StatutSession;
  /** Index de l'étape où l'on s'est arrêté (bouton « Reprendre », R-SES-06). */
  etape_courante: number;
  etapes_faites: number[];
  capsules_lues: string[];
  mode: ModeSession;
  commencee_le?: string;
  terminee_le?: string;
  duree_min?: number;
  /** Nombre de sessions « faible énergie » faites sur cette session (R-SES-10). */
  faible_energie: number;
}

/** Une session réalisée (standard ou faible énergie) : sert à la régularité (R-PLA-04). */
export interface Realisation {
  session: string;
  mode: ModeSession;
  le: string;
}

export interface Blocage {
  exercice: string;
  session: string;
  /** Horodatage du premier clic sur « Je suis bloqué » (T0, R-BLQ-01). */
  t0: string;
  paliers: number[];
  abandon_le?: string;
}

export interface Variante {
  exercice: string;
  pour_session: string;
  statut: 'planifiee' | 'terminee';
}

/** État de planification d'une carte (algorithme SM-2, R-CAR-03). */
export interface EtatCarte {
  actif_depuis: string;
  intervalle: number;
  facilite: number;
  repetitions: number;
  /** Date (AAAA-MM-JJ) à partir de laquelle la carte est due. */
  echeance: string;
  suspendue: boolean;
  manuelle: boolean;
  /** Recto/verso uniquement pour les cartes manuelles (les autres viennent du contenu). */
  recto?: string;
  verso?: string;
  domaine?: Domaine;
}

export type NoteRevision = 1 | 3 | 4 | 5;

export interface Revision {
  carte: string;
  note: NoteRevision;
  le: string;
}

export type TypeEntreeJournal =
  | 'cloture'
  | 'libre'
  | 'revue_sprint'
  | 'retro_sprint'
  | 'bilan_saison'
  | 'note_mentor';

export interface EntreeJournal {
  id: string;
  le: string;
  type: TypeEntreeJournal;
  texte: string;
  session?: string;
  sprint?: string;
  energie?: number;
  /** Pour les notes du mentor : review, defi, soutenance, bilan, libre (R-MEN-03). */
  sous_type?: string;
  archivee: boolean;
  modifiee_le?: string;
}

export type Colonne = 'a_faire' | 'en_cours' | 'fait';

export interface ElementTableau {
  id: string;
  titre: string;
  description?: string;
  domaine?: Domaine;
  colonne: Colonne;
  position: number;
  /** D'où vient l'élément : exercice de session, livrable, jalon, manuel. */
  source: 'exercice' | 'livrable' | 'jalon' | 'manuel';
  session?: string;
  sprint?: string;
  story?: { en_tant_que: string; je_veux: string; afin_de: string };
  criteres?: { texte: string; fait: boolean }[];
  cree_le: string;
}

export interface Sprint {
  id: string;
  numero: number;
  objectif: string;
  debut: string;
  fin: string;
  elements: string[];
  statut: 'planifie' | 'en_cours' | 'termine';
}

/**
 * La forme PUBLIQUE d'un livrable (R-PTF-05) : ce qui sera visible sur GitHub et LinkedIn.
 * Le marché de 2026 paie des preuves, pas des titres — c'est tout l'objet de ce bloc.
 */
export interface EtudeCas {
  statut: 'brouillon' | 'pret' | 'publie';
  /** Le README structuré : problème, utilisateurs, architecture, décisions, résultats, coûts. */
  readme: string;
  /** Le script de la démo vidéo de 3 minutes (écrit par le mentor, enregistré par Elias). */
  script_demo: string;
  /** Le brouillon de post LinkedIn, à relire et publier soi-même. */
  post: string;
  /**
   * La checklist de publication (R-PTF-06). Elle est BLOQUANTE : tant que les quatre ne sont
   * pas cochées, `statut` ne peut pas passer à `publie`. Un secret publié ne se dépublie pas.
   */
  checklist: { secrets: boolean; donnees: boolean; infra: boolean; relu: boolean };
  publiee_le?: string;
}

/** Une étude de cas neuve : tout à faire, rien de coché. */
export function etudeCasVide(): EtudeCas {
  return { statut: 'brouillon', readme: '', script_demo: '', post: '', checklist: { secrets: false, donnees: false, infra: false, relu: false } };
}

export type Couverture = 'couverte' | 'partielle' | 'absente';

/** Une entrée mensuelle de veille marché (R-VEI-01) : les offres collées, et ce qu'on en tire. */
export interface EntreeVeille {
  id: string;
  le: string;
  /** Le texte brut des offres, collé tel quel. */
  offres: string;
  /** Les compétences que le mentor en a extraites. */
  competences: string[];
}

/** Une ligne de la carte des écarts (R-VEI-02). */
export interface Ecart {
  couverture: Couverture;
  /** La saison qui couvre la compétence, si elle est couverte. */
  saison?: number;
  /** Les dates où la compétence est apparue dans une veille : trois de suite « absente » alertent. */
  vu_le: string[];
}

export interface Projet {
  id: string;
  titre: string;
  description: string;
  /** Saison d'origine ; 0 pour le livrable d'un parcours annexe (R-PAR-05). */
  saison: number;
  /** Identifiant du parcours annexe, si le projet en vient. */
  parcours?: string;
  statut: 'en_cours' | 'livre';
  depot?: string;
  demo?: string;
  captures: string[];
  /** La forme publique du livrable (R-PTF-05), absente tant qu'on n'y a pas touché. */
  etude_cas?: EtudeCas;
  cree_le: string;
}

export interface Parametres {
  /** Jours de progression, 1 = lundi … 7 = dimanche (R-PLA-01). */
  jours: number[];
  vendredi: boolean;
  /** Date de début de chaque saison, AAAA-MM-JJ (R-PRG-03). */
  debut_saisons: Record<string, string>;
  faible_energie_defaut: boolean;
  theme: 'auto' | 'clair' | 'sombre';
}

export interface Etat {
  version: 1;
  parametres: Parametres;
  saison_courante: number;
  progression: {
    sessions: Record<string, ProgressionSession>;
    realisations: Realisation[];
    defis: Record<string, { statut: 'propose' | 'fait' | 'passe'; fait_le?: string }>;
    soutenances: Record<string, { statut: 'a_faire' | 'faite'; faite_le?: string }>;
  };
  blocages: Blocage[];
  variantes: Variante[];
  cartes: Record<string, EtatCarte>;
  revisions: Revision[];
  journal: EntreeJournal[];
  tableau: {
    mode: 'kanban' | 'scrum';
    elements: Record<string, ElementTableau>;
    backlog_ordre: string[];
    sprints: Sprint[];
  };
  portfolio: Projet[];
  /** Parcours annexes activés (R-PAR-02) ; la progression de leurs sessions vit dans `progression.sessions`. */
  parcours: Record<string, { active_le: string }>;
  /** Veille marché mensuelle et carte des écarts (R-VEI-01, R-VEI-02). */
  veille: { entrees: EntreeVeille[]; ecarts: Record<string, Ecart> };
  semaines_off: string[];
}

/**
 * Complète un état lu ailleurs — instantané en base, IndexedDB d'un appareil — avec les champs
 * ajoutés depuis qu'il a été écrit.
 *
 * L'instantané de la table `etats` et celui du navigateur sont figés au moment où ils ont été
 * calculés. Un champ ajouté au modèle (`parcours` le 30/09, `veille` le même jour) n'y est donc
 * pas, et le premier accès plante : `Cannot read properties of undefined`. Rejouer les événements
 * règle le cas du serveur, jamais celui d'un appareil resté hors ligne.
 *
 * À appeler à CHAQUE lecture d'un état venu d'ailleurs.
 */
export function normaliserEtat(etat: Etat): Etat {
  const vide = etatInitial();
  return {
    ...vide,
    ...etat,
    parametres: { ...vide.parametres, ...etat.parametres },
    progression: { ...vide.progression, ...etat.progression },
    tableau: { ...vide.tableau, ...etat.tableau },
    veille: { ...vide.veille, ...etat.veille },
    parcours: etat.parcours ?? vide.parcours,
  };
}

/** L'état de départ, avant tout événement. */
export function etatInitial(): Etat {
  return {
    version: 1,
    parametres: {
      jours: [1, 3, 4],
      vendredi: true,
      debut_saisons: {},
      faible_energie_defaut: false,
      theme: 'auto',
    },
    saison_courante: 1,
    progression: { sessions: {}, realisations: [], defis: {}, soutenances: {} },
    blocages: [],
    variantes: [],
    cartes: {},
    revisions: [],
    journal: [],
    tableau: { mode: 'kanban', elements: {}, backlog_ordre: [], sprints: [] },
    portfolio: [],
    parcours: {},
    veille: { entrees: [], ecarts: {} },
    semaines_off: [],
  };
}
